import type {
  ConsoleRequest,
  SubjectGroup,
  TableMetadata,
  TableSubject,
} from "./types";

// A non-2xx response while loading settings. The message is the HTTP status
// followed by the response body exactly as the API (or gateway) sent it.
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: string,
  ) {
    super(`${status}: ${body}`);
  }
}

export async function getSubjects(signal?: AbortSignal): Promise<SubjectGroup[]> {
  return getJson<SubjectGroup[]>("/backend/subjects?format=json", signal);
}

export async function getTables(
  subjectId: string,
  signal?: AbortSignal,
): Promise<TableSubject[]> {
  return getJson<TableSubject[]>(
    `/backend/tables?format=json&subject_id=${encodeURIComponent(subjectId)}`,
    signal,
  );
}

export async function getTableMetadata(
  tableId: string,
  signal?: AbortSignal,
): Promise<TableMetadata> {
  return getJson<TableMetadata>(
    `/backend/table/${encodeURIComponent(tableId)}?format=json`,
    signal,
  );
}

export async function executeConsoleRequest(request: ConsoleRequest): Promise<Response> {
  return fetch(request.backendPath, {
    method: request.method,
    headers: {
      Accept: "application/json, text/csv;q=0.9",
      ...(request.body ? { "Content-Type": "application/json" } : {}),
    },
    ...(request.body ? { body: JSON.stringify(request.body) } : {}),
  });
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, { signal, headers: { Accept: "application/json" } });
  if (!response.ok) await throwResponseError(response);
  return response.json() as Promise<T>;
}

async function throwResponseError(response: Response): Promise<never> {
  const body = (await response.text()).trim();
  throw new ApiError(response.status, body || response.statusText || "(tomt svar)");
}
